/**
 * FinanceApp - Copyright (c) 2026 Emmanuel TESTE
 * Tous droits réservés - Logiciel propriétaire
 */
import { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Menu, Search, ShieldCheck, Building2,
  ChevronDown, User, Settings, Lock, LogOut, Sparkles, HelpCircle,
} from "lucide-react";
import api from "../services/api";
import NotificationBell from "./notifications/NotificationBell";
import ProblemReportButton from "./ProblemReportButton";

export default function Topbar({ pageLabel, currentPage, onMenuToggle, currentSociete, onNavigate }) {
  const { user, logout } = useAuth();
  const [search, setSearch]     = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  // Nouveautés non vues depuis la dernière visite de la page « Nouveautés »
  const [unseen, setUnseen] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () => api.help.changelog().then(d => { if (alive) setUnseen(d?.unseen || 0); }).catch(() => {});
    load();
    const onSeen = () => setUnseen(0);
    window.addEventListener("execpro360:changelog-seen", onSeen);
    return () => { alive = false; window.removeEventListener("execpro360:changelog-seen", onSeen); };
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const initials = user?.name
    ? user.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()
    : "??";

  const roleLabel = user?.role === "admin" ? "Admin" : user?.role === "comptable" ? "Comptable" : user?.role || "";

  return (
    <header style={{
      height: "var(--topbar-height)",
      background: "var(--bg-card)",
      borderBottom: "var(--border)",
      display: "flex",
      alignItems: "center",
      padding: "0 20px",
      gap: 12,
      flexShrink: 0,
      zIndex: 10,
    }}>
      {/* Toggle sidebar */}
      <button
        onClick={onMenuToggle}
        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 6, display: "flex", borderRadius: 8 }}
        aria-label="Menu"
      >
        <Menu size={18} />
      </button>

      {/* Titre page */}
      <h1 style={{ fontSize: 15, fontWeight: 700, flex: 1, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {pageLabel}
      </h1>

      {/* Barre de recherche */}
      <div style={{ display: "flex", alignItems: "center", gap: 7, background: "var(--bg-secondary)", border: "var(--border)", borderRadius: 8, padding: "6px 12px", width: 200 }}>
        <Search size={14} color="var(--text-muted)" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher…"
          style={{ border: "none", background: "none", outline: "none", fontSize: 13, color: "var(--text-primary)", flex: 1, width: 0 }}
        />
      </div>

      {/* Badge sécurité */}
      <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(16,185,129,0.1)", padding: "5px 10px", borderRadius: 8, fontSize: 12, color: "var(--success)", fontWeight: 500, flexShrink: 0 }}>
        <ShieldCheck size={13} />
        <span>Sécurisé · 2FA</span>
      </div>

      {/* Société active */}
      {currentSociete && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", background: "var(--bg-secondary)", border: "var(--border)", borderRadius: 8, fontSize: 12, color: "var(--text-secondary)", flexShrink: 0, maxWidth: 160, overflow: "hidden" }}>
          <Building2 size={13} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{currentSociete}</span>
        </div>
      )}

      {/* Signaler un problème (administrateur et comptable) */}
      <ProblemReportButton currentPage={currentPage} pageLabel={pageLabel} />

      {/* Cloche notifications intelligente */}
      <NotificationBell onNavigate={onNavigate} />

      {/* Menu utilisateur */}
      <div ref={menuRef} style={{ position: "relative", flexShrink: 0 }}>
        <div
          onClick={() => setMenuOpen(o => !o)}
          style={{
            display: "flex", alignItems: "center", gap: 8, position: "relative",
            padding: "5px 10px 5px 6px",
            border: "var(--border)",
            borderRadius: 8, cursor: "pointer",
            background: menuOpen ? "var(--bg-secondary)" : "var(--bg-card)",
            userSelect: "none",
          }}
        >
          <div style={{
            width: 28, height: 28, borderRadius: 7,
            background: "var(--accent)", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 700, fontSize: 11, flexShrink: 0,
          }}>
            {initials}
          </div>
          {unseen > 0 && <span title={`${unseen} nouveauté(s) non vue(s)`} aria-label={`${unseen} nouveauté(s) non vue(s)`}
            style={{ position: "absolute", top: 3, left: 28, width: 9, height: 9, borderRadius: 99, background: "#f59e0b", border: "2px solid var(--bg-card)" }} />}
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>{user?.name || "Utilisateur"}</div>
            <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{roleLabel}</div>
          </div>
          <ChevronDown size={13} color="var(--text-muted)" style={{ marginLeft: 2, transition: "transform 0.2s", transform: menuOpen ? "rotate(180deg)" : "none" }} />
        </div>

        {menuOpen && (
          <div style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0, width: 220,
            background: "var(--bg-card)", border: "var(--border)",
            borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
            zIndex: 200, overflow: "hidden",
          }}>
            {/* Entête utilisateur */}
            <div style={{ padding: "12px 14px 10px", borderBottom: "var(--border)" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{user?.name}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{user?.email}</div>
            </div>

            {/* Entrées du menu */}
            {[
              { Icon: User,     label: "Mon profil",           action: () => { onNavigate?.("securite"); setMenuOpen(false); } },
              { Icon: Building2, label: "Ma société",          action: () => { onNavigate?.("societes");  setMenuOpen(false); } },
              { Icon: Settings, label: "Paramètres",           action: () => { onNavigate?.("modules");  setMenuOpen(false); } },
              { Icon: Lock,     label: "Changer mot de passe", action: () => { onNavigate?.("securite"); setMenuOpen(false); } },
              { Icon: Sparkles, label: "Nouveautés", badge: unseen, action: () => { onNavigate?.("nouveautes"); setMenuOpen(false); } },
              { Icon: HelpCircle, label: "Aide et guides", action: () => { onNavigate?.("aide"); setMenuOpen(false); } },
            ].map(item => (
              <div
                key={item.label}
                onClick={item.action}
                style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 14px", cursor: "pointer", fontSize: 13, color: "var(--text-primary)" }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--bg-secondary)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <item.Icon size={15} color="var(--text-muted)" style={{ flexShrink: 0 }} />
                {item.label}
                {item.badge > 0 && <span style={{ marginLeft: "auto", fontSize: 10.5, fontWeight: 700, padding: "1px 7px", borderRadius: 99, background: "#fef3c7", color: "#92400e" }}>{item.badge}</span>}
              </div>
            ))}

            <div style={{ borderTop: "var(--border)", margin: "2px 0" }} />

            <div
              onClick={() => { logout(); setMenuOpen(false); }}
              style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 14px", cursor: "pointer", fontSize: 13, color: "var(--danger)" }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(239,68,68,0.08)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <LogOut size={15} color="var(--danger)" style={{ flexShrink: 0 }} />
              Se déconnecter
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
